import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  BookOpen,
  Briefcase,
  CalendarClock,
  CheckCircle2,
  FileText,
  Home,
  Scale,
  ShieldCheck,
  UserCheck,
  X,
} from 'lucide-react';

interface PolitiqueTeletravailBZModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'synthese' | 'politique' | 'gouvernance' | 'suivis';

const policyPdfPath = '/documents/admin-rh/politique-simplifiee-teletravail-bz-v6-2026-07-22.pdf';

const tabs: { id: TabId; label: string; icon: typeof BookOpen }[] = [
  { id: 'synthese', label: 'Synthèse', icon: BookOpen },
  { id: 'politique', label: 'Politique simplifiée', icon: FileText },
  { id: 'gouvernance', label: 'Gouvernance', icon: Scale },
  { id: 'suivis', label: 'Suivis administratifs', icon: CalendarClock },
];

const principles = [
  {
    title: 'Privilège encadré',
    text: 'Le télétravail n’est pas un droit acquis. Il demeure autorisé par BZ selon les besoins de l’entreprise.',
    icon: ShieldCheck,
  },
  {
    title: 'Équité interne',
    text: 'Les critères sont communs et cohérents, mais les modalités peuvent varier selon les postes et les contraintes opérationnelles.',
    icon: Scale,
  },
  {
    title: 'Rendement équivalent',
    text: 'La disponibilité, la productivité, la communication et la qualité attendues restent équivalentes au présentiel.',
    icon: CheckCircle2,
  },
];

const ruleCards = [
  { label: 'Accès standard', value: '1 jour / semaine max', note: 'Règle générale BZ, avec exceptions possibles si autorisées.' },
  { label: 'Moment admissible', value: 'Après probation', note: 'Probation complétée avec succès.' },
  { label: 'Planification', value: 'À l’avance', note: 'La journée doit être planifiée et compatible avec l’équipe.' },
  { label: 'Révision', value: 'En tout temps', note: 'BZ peut modifier, suspendre ou retirer l’autorisation.' },
];

const raci = [
  {
    role: 'Responsable / approbateur',
    owner: 'Supérieur immédiat',
    duty: 'Autoriser, suivre et réviser les modalités de télétravail de son équipe.',
  },
  {
    role: 'Escalade',
    owner: 'RH / Direction',
    duty: 'Intervenir en soutien lorsqu’un cas devient problématique, sensible ou exceptionnel.',
  },
  {
    role: 'Responsable employé',
    owner: 'Employé',
    duty: 'Respecter les conditions, rester disponible, protéger les informations et se présenter au bureau lorsque requis.',
  },
  {
    role: 'Consultés',
    owner: 'RH / Administration / TI',
    duty: 'Soutenir l’application, les communications, la confidentialité, les accès et la conservation des preuves.',
  },
];

const eligibility = [
  'Viser l’ensemble des employés BZ pouvant être autorisés au télétravail.',
  'Avoir complété la probation avec succès.',
  'Occuper un poste compatible avec le télétravail.',
  'Démontrer l’autonomie, le rendement, la disponibilité et la collaboration attendus.',
  'Obtenir une autorisation préalable selon les balises de son équipe.',
];

const followUps = [
  'Ajouter une référence dans le Cahier des politiques BZ vers la politique officielle de télétravail.',
  'Valider si une annexe “conditions minimales pour conserver le privilège de télétravail” est requise.',
  'Revoir la section télétravail du Cahier pour éviter les doublons ou contradictions.',
  'Décider séparément si un accusé de réception ou une signature TactiCE est requis en plus de la diffusion déjà prouvée.',
];

const diffusionProof = [
  'Confirmation vocale Louis-Alexandre Hébert reçue le 2026-08-25 à 15:41 EDT.',
  'Diffusion par courriel au groupe GBZ de 58 employés, avec la politique cartes de crédit et comptes de dépenses BZ.',
  'Copies .eml conservées: copie d’envoi, copie reçue, Message-ID Outlook et pièces jointes vérifiées.',
  'État PRIsME: diffusion officielle documentée et preuve complète.',
];

function SyntheseTab() {
  return (
    <div className="space-y-6">
      <section className="grid gap-5 md:grid-cols-3">
        {principles.map((principle) => {
          const Icon = principle.icon;
          return (
            <div key={principle.title} className="rounded-2xl border border-cyan-300/25 bg-cyan-400/10 p-5">
              <Icon className="mb-3 h-8 w-8 text-cyan-200" />
              <h3 className="text-xl font-bold">{principle.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">{principle.text}</p>
            </div>
          );
        })}
      </section>

      <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
        <div className="mb-5 flex items-center gap-3">
          <Home className="h-7 w-7 text-cyan-200" />
          <h3 className="text-2xl font-bold">Règle de base générale</h3>
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {ruleCards.map((item) => (
            <div key={item.label} className="rounded-2xl border border-white/10 bg-slate-900/70 p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{item.label}</p>
              <p className="mt-2 text-xl font-black text-white">{item.value}</p>
              <p className="mt-2 text-xs leading-relaxed text-slate-300">{item.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-emerald-300/25 bg-emerald-500/10 p-6">
          <div className="mb-4 flex items-center gap-3">
            <CheckCircle2 className="h-7 w-7 text-emerald-200" />
            <h3 className="text-2xl font-bold">Admissibilité</h3>
          </div>
          <ul className="space-y-3 text-sm leading-relaxed text-emerald-50/90">
            {eligibility.map((item) => (
              <li key={item} className="flex gap-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-200" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-3xl border border-amber-300/25 bg-amber-500/10 p-6">
          <div className="mb-4 flex items-center gap-3">
            <AlertTriangle className="h-7 w-7 text-amber-200" />
            <h3 className="text-2xl font-bold">Limites à garder claires</h3>
          </div>
          <ul className="space-y-3 text-sm leading-relaxed text-amber-50/90">
            <li>Le télétravail ne remplace pas une maladie, un congé ou une obligation personnelle.</li>
            <li>BZ peut exiger une présence au bureau selon les besoins clients, l’équipe ou la direction.</li>
            <li>Tout télétravail ailleurs qu’au domicile, pour un poste technique, exige une demande écrite préalable.</li>
            <li>La confidentialité, les équipements et les règles de cybersécurité s’appliquent pleinement hors bureau.</li>
          </ul>
        </div>
      </section>

      <section className="rounded-3xl border border-emerald-300/25 bg-emerald-500/10 p-6">
        <div className="mb-4 flex items-center gap-3">
          <CheckCircle2 className="h-7 w-7 text-emerald-200" />
          <h3 className="text-2xl font-bold">Diffusion officielle documentée</h3>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {diffusionProof.map((item) => (
            <div key={item} className="flex items-start gap-3 rounded-2xl border border-emerald-200/20 bg-slate-900/65 p-5 text-sm leading-relaxed text-emerald-50/90">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-200" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function PolitiqueTab() {
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
      <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm font-semibold text-cyan-100">
            <FileText className="h-4 w-4" />
            Version de référence v6 · Juillet 2026
          </div>
          <h3 className="text-2xl font-bold">One-pager — politique simplifiée</h3>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-300">
            Document de référence intégré à cette card PRIsME. Il complète la section “Politique télétravail” du Cahier
            des politiques BZ et devra être relié au Cahier lors d’une prochaine mise à jour.
          </p>
        </div>
        <a
          href={policyPdfPath}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-400 px-4 py-3 text-sm font-bold text-slate-950 transition hover:bg-cyan-300"
        >
          <FileText className="h-4 w-4" />
          Ouvrir le PDF
        </a>
      </div>
      <div className="h-[72vh] overflow-hidden rounded-2xl border border-slate-700 bg-slate-900">
        <iframe title="Politique simplifiée de télétravail BZ" src={policyPdfPath} className="h-full w-full" />
      </div>
    </section>
  );
}

function GouvernanceTab() {
  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-violet-300/25 bg-violet-500/10 p-6">
        <div className="mb-5 flex items-center gap-3">
          <UserCheck className="h-7 w-7 text-violet-200" />
          <h3 className="text-2xl font-bold">RACI de gouvernance</h3>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {raci.map((item) => (
            <div key={item.role} className="rounded-2xl border border-violet-200/20 bg-slate-900/65 p-5">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-violet-200">{item.role}</p>
              <h4 className="mt-2 text-lg font-bold">{item.owner}</h4>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">{item.duty}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border border-slate-600 bg-slate-900/80 p-6">
        <div className="mb-5 flex items-center gap-3">
          <Briefcase className="h-7 w-7 text-slate-200" />
          <h3 className="text-2xl font-bold">Positionnement PRIsME</h3>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-sm font-semibold text-cyan-100">Domaine</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">Administration & RH</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-sm font-semibold text-cyan-100">Nature</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">Politique interne BZ en vigueur dans PRIsME.</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-sm font-semibold text-cyan-100">Lien TactiCE</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">Diffusion officielle déjà documentée dans PRIsME; accusé de réception ou signature TactiCE à décider séparément.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

function SuivisTab() {
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
      <div className="mb-5 flex items-center gap-3">
        <CalendarClock className="h-7 w-7 text-cyan-200" />
        <h3 className="text-2xl font-bold">Suivis administratifs</h3>
      </div>
      <div className="mb-5 rounded-2xl border border-emerald-300/25 bg-emerald-500/10 p-5">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-200" />
          <p className="text-sm leading-relaxed text-emerald-50/90">
            La preuve de diffusion du 2026-08-25 est complète: confirmation vocale, groupe GBZ 58 employés, copie d’envoi, copie reçue et pièces jointes vérifiées.
          </p>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {followUps.map((item) => (
          <div key={item} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-slate-900/70 p-5 text-sm leading-relaxed text-slate-200">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cyan-200" />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function PolitiqueTeletravailBZModal({ isOpen, onClose }: PolitiqueTeletravailBZModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('synthese');

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ type: 'spring', damping: 24, stiffness: 220 }}
            className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-3xl border border-cyan-300/30 bg-slate-950 text-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 border-b border-white/10 bg-slate-950/95 px-8 py-6 backdrop-blur">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm font-semibold text-cyan-100">
                    <Home className="h-4 w-4" />
                    Administration & RH · Politique interne · En vigueur
                  </div>
                  <h2 className="text-3xl font-black tracking-tight md:text-4xl">Politique télétravail BZ</h2>
                  <p className="mt-3 max-w-4xl text-base leading-relaxed text-slate-300">
                    Carte PRIsME pour cadrer le télétravail comme privilège encadré, équitable et aligné sur les besoins
                    opérationnels de BZ. Cette version reprend le one-pager de référence v6 et est en vigueur dans PRIsME.
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-full border border-white/10 bg-white/10 p-3 text-white transition hover:bg-white/20"
                  aria-label="Fermer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = tab.id === activeTab;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                        isActive
                          ? 'border-cyan-300 bg-cyan-300 text-slate-950'
                          : 'border-white/10 bg-white/[0.04] text-slate-300 hover:border-cyan-300/40 hover:bg-cyan-300/10'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="px-8 py-8">
              {activeTab === 'synthese' && <SyntheseTab />}
              {activeTab === 'politique' && <PolitiqueTab />}
              {activeTab === 'gouvernance' && <GouvernanceTab />}
              {activeTab === 'suivis' && <SuivisTab />}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
